import { useSelector } from "react-redux";
import { useRole } from "./use-role";

export const AUTH_LEVEL = {
    READ_ONLY: 1,
    WRITE: 2,
    FULL_ACCESS: 3,
};

export const normalizePermission = (permissionValue) => {
    if (!permissionValue || Array.isArray(permissionValue) || typeof permissionValue !== "object") {
        return {};
    }

    return permissionValue;
};

export const useAuth = (code, level = AUTH_LEVEL.READ_ONLY) => {
    const role = useRole();
    const permission = useSelector((state) => state.Auth.permission);
    const normalizedPermission = normalizePermission(permission);

    return role === 1 || (normalizedPermission[code] || 0) >= level;
};

/**
 * Profile permission code behind the AI Assistant surfaces. Declared once so the
 * context cards, the floating launcher, the sidebar entry and the route guard
 * cannot drift apart.
 */
export const AI_ASSISTANT_PERMISSION = "facility_ai_chat";

/**
 * AI Assistant access is profile-driven only.
 *
 * `useAuth` short-circuits on `role === 1` (super admin sees everything), but the
 * AI Assistant surfaces must stay hidden for a role-1 account whose profile was
 * never granted `facility_ai_chat`. Every AI Assistant entry point therefore uses
 * this hook instead of `useAuth("facility_ai_chat")`.
 */
export const useAiAssistantAccess = () => {
    const permission = useSelector((state) => state.Auth.permission);
    const normalizedPermission = normalizePermission(permission);

    return (normalizedPermission[AI_ASSISTANT_PERMISSION] || 0) >= AUTH_LEVEL.READ_ONLY;
};

export const useAuthReadOnly = (code) => {
    return useAuth(code, AUTH_LEVEL.READ_ONLY);
};

export const useAuthEditable = (code) => {
    return useAuth(code, AUTH_LEVEL.WRITE);
};

export const useAuthFullAccess = (code) => {
    return useAuth(code, AUTH_LEVEL.FULL_ACCESS);
};
